import type {
  Block, Cell, CmpItem, CmpSide, Column, KvItem, NoteBlock, TableBlock, Tone,
} from '@/kova/spec/types'

import type { ApiClient } from '@/lib/api'
import { ApiError } from '@/lib/api/errors'
import { exactMinor, exactMinorBig, format, isCurrencyCode, money } from '@/lib/money/money'

import { accountStateBlocks, type AccountStanding } from './accountstate'
import { handleClaimsBand, handleClaimsPath, isHandleClaimsResponse } from './handleclaims'
import { customerMappingsPath, mappingsBand } from './mappings'
import type { Caller } from './handles'
import { callerBeside } from './writes'
import type { DataSource, ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'

/**
 * Customers › Customer 360 (`cust-360`), read from the four per-customer GETs
 * that go-wallet#425 shipped (handler `internal/server/admin_customers.go`):
 * the profile, the accounts the institution issued, the journal as it touched
 * this customer, and what the institution reported about them.
 *
 * This is the screen support opens when somebody rings to say their money has
 * not arrived, which sets the standard the whole file is written to: every
 * sentence on it is one an agent may repeat to a customer on the phone. A
 * figure that is wrong in a way that looks plausible is therefore worse here
 * than a figure that is missing and says so, and the composition below prefers
 * the second every time.
 *
 * # Four reads, four bands, and no band may take another down
 *
 * The reads are issued together and each one's answer is folded in on its own.
 * A journal query that timed out must not cost the operator the profile they
 * needed to confirm who they are speaking to, and an accounts read that failed
 * must not hide the provider events that would have explained the delay. So
 * each band renders either its rows or the account of why it has none, exactly
 * the way the Today board degrades card by card, and the screen's provenance
 * chip names which bands are missing rather than claiming the page is live.
 *
 * # The absences on this screen are not interchangeable
 *
 * Five distinct "nothings" reach this file and each sends the operator
 * somewhere different. Collapsing any pair produces a false sentence about a
 * real customer:
 *
 *   - **No such customer.** The profile answered 404. That is an answer, and a
 *     definite one — nobody holds that id. It is not a screen that failed.
 *   - **A band that could not be read.** The endpoint answered 500, or the
 *     network did not carry. Nothing is known about that band, and an empty
 *     table would say the opposite.
 *   - **A customer with no ledger account.** A real, ordinary state: they
 *     exist, the institution has issued them nothing yet. `complete: true` with
 *     an empty list is the backend saying so, and it is why that flag is read
 *     rather than assumed.
 *   - **An account with no account number.** `iban_status: "pending"` — the
 *     account exists before its number does (#45). Absent, never blank: a blank
 *     cell reads as a field nobody filled in rather than a number not yet
 *     issued.
 *   - **A figure the backend would not state.** `availability` is not
 *     `available`, and the reason travels with it. Never rendered as zero,
 *     because "€0.00" and "we could not read it" are different sentences and
 *     only one of them means the customer has no money.
 *
 * # No control, by construction
 *
 * Restrict and restore exist on the backend, are `PermAccountRestrict`, and are
 * Ops (go-wallet#135). They are deliberately absent here: #42 is the read side,
 * and a control belongs in a change that can argue for it rather than one that
 * inherits it. There is no mutating block kind in anything this file returns.
 * Account `state` — including `frozen` — is rendered as a read-only badge with
 * the institution's attribution beside it, which is console law 1.
 */

// ---------------------------------------------------------------------------
// The wire shape — mirrors internal/server/admin_customers.go json tags
// ---------------------------------------------------------------------------

/**
 * Whether a figure has a value at all.
 *
 * `not_built` and `unavailable` are deliberately not one state: the first is a
 * roadmap fact and the second is an incident, and an operator does something
 * different about each.
 */
export type Availability = 'available' | 'not_built' | 'unavailable'

/** Where a figure's truth comes from. Invariant 2, made visible per figure. */
export type Authority = 'operational' | 'provider-confirmed' | 'analytics'

export interface ProvenanceView {
  readonly source: string
  readonly authority: string
  readonly legal: string
  readonly read_at: string
}

/**
 * Minor units and an explicit ISO code, always together (invariants 3 and 4).
 *
 * `amount_minor` is `number | string` because the admin surface is mid-flip:
 * it serialises an `int64` as a JSON number today and go-wallet#411 moves it to
 * a decimal string, for the reason go-wallet#377 already moved the customer
 * surface — `JSON.parse` rounds past 2^53 before any code here runs. The
 * backend's own comment says it cannot flip until the console reads both forms.
 * Reading both is what makes that flip a non-event instead of an outage.
 */
export interface MoneyView {
  readonly amount_minor: number | string
  readonly currency: string
}

export interface FigureView {
  readonly label: string
  readonly availability: string
  readonly reason?: string
  /** Absent, never zero, when the figure is not available. */
  readonly value?: MoneyView
  readonly provenance?: ProvenanceView
}

/**
 * The cap that was applied, travelling with the rows.
 *
 * `has_more` is answered by reading one row past the cap, never by counting the
 * table, and `next_cursor` is present only when there is more — a cursor on a
 * final page is a button that leads nowhere.
 */
export interface PageView {
  readonly limit: number
  readonly returned: number
  readonly has_more: boolean
  readonly next_cursor?: number
}

/**
 * One `customers` row.
 *
 * Every nullable column is optional here because on this screen each absence is
 * a distinction an operator acts on. No institution subject reference means the
 * customer has never been submitted for a decision — which is itself the answer
 * to "why has nothing happened". No SLA due date means they are not on the
 * clock. A zero value would report both as facts we hold.
 *
 * There is no identity-provider subject id and no credential-shaped field of
 * any kind, and there must never be one: the backend deliberately does not
 * select it (go-wallet CLAUDE.md §6, invariant 13), and a console that added a
 * field the backend does not send would be inventing a data boundary breach.
 */
export interface CustomerView {
  readonly id: number
  readonly kind: string
  readonly country_code: string
  readonly legal_name: string
  readonly email: string
  readonly institution_subject_ref?: string
  /** The institution's verdict as we recorded it. Never ours (invariant 2). */
  readonly acceptance: string
  readonly acceptance_reason?: string
  readonly state_entered_at: string
  readonly sla_due_at?: string
  readonly created_at: string
  readonly updated_at: string
  readonly disabled_at?: string
  readonly offboarded: boolean
}

export interface CustomerProfileResponse {
  readonly generated_at: string
  readonly customer: CustomerView
}

/** One account the institution issued, with the journal position that belongs to it. */
export interface CustomerAccountView {
  readonly id: number
  readonly institution_account_ref: string
  readonly currency: string
  /** `issued` or `pending`. An account exists before its number does (#45). */
  readonly iban_status: string
  readonly iban_masked?: string
  readonly iban_note?: string
  readonly bic?: string
  readonly holder_name?: string
  readonly state: string
  readonly state_source: string
  readonly state_reason?: string
  readonly state_entered_at: string
  readonly opened_at?: string
  readonly created_at: string
  readonly closed_at?: string
  readonly ledger_account_id?: number
  /**
   * Open ledger wallet accounts this customer holds in this currency, or
   * `null` when the backend did not state a number this console could read.
   */
  readonly wallet_matches: number | null
  readonly ledger: FigureView
  readonly provider: FigureView
  readonly delta: FigureView
  readonly balance_updated_at?: string
  readonly last_posting_id?: number
  readonly provider_as_of?: string
  /**
   * True only when both sides were read AND agree; `null` when the backend
   * did not state it as a boolean. Never coerced — see `readAccount`.
   */
  readonly reconciled: boolean | null
}

/**
 * Every account this customer holds — and it says so rather than paging.
 *
 * `complete` is read rather than assumed. It is the only thing that separates
 * "this customer holds no account" from "this list was cut short", and the two
 * lead an operator to opposite conclusions about why a payment bounced.
 */
export interface CustomerAccountsResponse {
  readonly generated_at: string
  readonly complete: boolean
  readonly note: string
  readonly accounts: readonly CustomerAccountView[]
}

export interface LedgerLegView {
  readonly id: number
  readonly account_id: number
  readonly account_class: string
  readonly account_scope: string
  readonly amount: FigureView
}

/** One journal entry, with what it did to THIS customer. */
export interface CustomerPostingView {
  readonly id: number
  readonly kind: string
  readonly posting_type: string
  readonly currency: string
  /** Absent rather than `""` when there is none — FR-5.5 / invariant 2b. */
  readonly provider_ref?: string
  readonly idempotency_key?: string
  readonly corrects_posting_id?: number
  readonly reverses_posting_id?: number
  readonly occurred_at: string
  readonly posted_at: string
  readonly actor: string
  readonly reason_code?: string
  /** Detail view only. Absent on a list — the list did not look. */
  readonly touches_customer_value?: boolean
  readonly legs?: readonly LedgerLegView[]
  /** The sum of this posting's legs on this customer's wallet accounts. */
  readonly net: FigureView
}

export interface CustomerPostingsResponse {
  readonly generated_at: string
  readonly page: PageView
  /** Why there is no running balance. Rendered verbatim. */
  readonly running_balance_note: string
  readonly postings: readonly CustomerPostingView[]
}

/**
 * One thing the institution told us about this customer's accounts.
 *
 * `institution_account_id` is the INSTITUTION's account, not the ledger
 * account: two tables, two id spaces, named apart on the wire so a console
 * cannot deep-link one into the other's screen. Nothing here is summed and
 * nothing derived from it is a balance — that is the accounts band's job, and
 * it is capped by the institution's own figure (invariant 2c).
 */
export interface CustomerEventView {
  readonly id: number
  readonly institution_account_id: number
  readonly provider: string
  readonly institution_event_id: string
  readonly institution_sequence?: number
  readonly state: string
  readonly direction: string
  readonly rail: string
  readonly authorised: FigureView
  readonly settled: FigureView
  /** The institution's booking date. Statements book on this (invariant 12). */
  readonly value_date: string
  readonly posted_at: string
  readonly observed_at: string
  readonly counterparty_name?: string
  readonly counterparty_iban_masked?: string
  readonly counterparty_bic?: string
  readonly remittance_unstructured?: string
  readonly remittance_structured?: string
  readonly end_to_end_id?: string
}

export interface CustomerEventsResponse {
  readonly generated_at: string
  readonly page: PageView
  readonly events: readonly CustomerEventView[]
}

// ---------------------------------------------------------------------------
// The endpoints
// ---------------------------------------------------------------------------

/** The directory root. Every read below hangs off it. */
export const CUSTOMERS_PATH = '/customers'

export function customerProfilePath(id: number): string {
  return `${CUSTOMERS_PATH}/${String(id)}`
}

export function customerAccountsPath(id: number): string {
  return `${customerProfilePath(id)}/accounts`
}

export function customerPostingsPath(id: number): string {
  return `${customerProfilePath(id)}/postings`
}

export function customerEventsPath(id: number): string {
  return `${customerProfilePath(id)}/events`
}

const SOURCE =
  'postgres · customers + accounts + ledger_postings + provider_events ' +
  '(GET /v1/admin/customers/{id} + /accounts + /postings + /events)'

// ---------------------------------------------------------------------------
// Reading the wire, fail-closed
// ---------------------------------------------------------------------------

const AVAILABILITIES = new Set<string>(['available', 'not_built', 'unavailable'])

/**
 * An availability this console does not know becomes `unavailable`.
 *
 * The direction is the point: an unrecognised value resolves to the reading
 * that shows the operator LESS confidence, never more. A fourth state added
 * upstream and defaulted to "available" would put an unverified number on a
 * screen an agent quotes from while somebody is on the phone.
 */
function availabilityOf(raw: string): Availability {
  return AVAILABILITIES.has(raw) ? (raw as Availability) : 'unavailable'
}

/** The backend's own words, verbatim, or an honest sentence when it sent none. */
function reasonFor(availability: Availability, raw: string, reason: string | undefined): string {
  if (reason !== undefined && reason !== '') return reason
  if (availability === 'available') return ''
  if (!AVAILABILITIES.has(raw)) {
    return `the backend reported availability ${JSON.stringify(raw)}, which this console does not know, so it is treated as unreadable`
  }
  return 'could not be read, and this is not an amount of zero'
}

function asRecord(raw: unknown): Record<string, unknown> | null {
  return typeof raw === 'object' && raw !== null && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : null
}

/**
 * A plain integer we can state exactly, or nothing.
 *
 * Used for ids and page counters. An id past 2^53 has already lost its last
 * digits inside `JSON.parse`, and an id wrong in its last digits is a different
 * customer, a different posting, a different event — on this screen, the wrong
 * person's money described to the person on the phone.
 */
function exactInteger(value: unknown): number | null {
  if (typeof value !== 'number') return null
  return Number.isSafeInteger(value) ? value : null
}

/**
 * An instant off the wire, in the four states it can arrive in.
 *
 * The zero one is not defensive. Go's zero `time.Time` serialises as
 * `0001-01-01T00:00:00Z` and parses perfectly, so a nullable column handed back
 * as a non-pointer renders as a date two thousand years old — which reads as a
 * finding rather than as the absence it is.
 */
type Instant =
  | { readonly kind: 'absent' }
  | { readonly kind: 'zero' }
  | { readonly kind: 'unparseable'; readonly raw: string }
  | { readonly kind: 'at'; readonly at: Date; readonly text: string }

function instantOf(iso: unknown): Instant {
  if (typeof iso !== 'string' || iso === '') return { kind: 'absent' }
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return { kind: 'unparseable', raw: iso }
  if (at.getUTCFullYear() <= 1) return { kind: 'zero' }
  return { kind: 'at', at, text: readAtStamp(at) }
}

/**
 * A timestamp for display, or an empty string when there is no date.
 *
 * An unparseable stamp is returned verbatim rather than replaced with an
 * invented one: an ugly string is recoverable, and a confidently wrong date
 * beside a payment is what an operator repeats to a customer.
 */
function stamp(iso: unknown): string {
  const parsed = instantOf(iso)
  switch (parsed.kind) {
    case 'at': return parsed.text
    case 'unparseable': return parsed.raw
    case 'zero': return 'no date recorded (the backend sent the zero instant)'
    case 'absent': return ''
  }
}

/** A timestamp, or a stated absence — never a blank cell that reads as unfilled. */
function stampOr(iso: unknown, absent: string): string {
  const text = stamp(iso)
  return text === '' ? absent : text
}

/** A string field that is genuinely present, or nothing. */
function text(raw: unknown): string | null {
  return typeof raw === 'string' && raw !== '' ? raw : null
}

// ---------------------------------------------------------------------------
// Placing a money figure
// ---------------------------------------------------------------------------

/**
 * Whether one figure may be shown as an amount, and if not, why not.
 *
 * ONE decision, asked by the table, by the per-account comparison and by the
 * screen's provenance chip, so the three cannot disagree about what the
 * operator was told. Three of the refusals fire while the backend says
 * `available` — an analytics authority, a value it did not send, an amount
 * whose currency cannot be named — and each of them silently removes one side
 * of a reconciliation. A leg quietly absent under a `Live` badge is the same
 * lie as a fabricated zero, told by omission instead.
 *
 * # Why the result carries a `bigint` AND a possibly-null `number`
 *
 * They answer different questions and the difference is load-bearing. The
 * design language's amount cell is `[minor: number, currency]`, so a table cell
 * cannot carry an integer past 2^53 at all — that is `exactMinor`'s bound and
 * why it has one. Text is not so limited, so `exactMinorBig` states the figure
 * exactly in the comparison band even when the cell above it cannot. Refusing
 * to state a figure we can state exactly would be its own invented absence, so
 * the out-of-range case renders the true amount as text rather than as nothing.
 *
 * Neither path routes a string through `Number()` first, which is the entire
 * trap: `Number("9007199254740993")` is `9007199254740992` and passes
 * `Number.isSafeInteger`, so a guard applied after the coercion accepts exactly
 * the value it exists to refuse. Both helpers check in BigInt, where nothing
 * rounds.
 */
type Placement =
  | {
      readonly ok: true
      /** `null` when exact, but beyond what an amount cell can hold. */
      readonly minor: number | null
      readonly currency: string
      /** The formatted amount, exact at any magnitude. */
      readonly text: string
    }
  | { readonly ok: false; readonly reason: string }

function placeFigure(f: FigureView): Placement {
  const availability = availabilityOf(f.availability)
  if (availability !== 'available') {
    return { ok: false, reason: reasonFor(availability, f.availability, f.reason) }
  }

  // `=== undefined` is not enough. `value: null` is typed away by the interface
  // and arrives all the same — Go's `omitempty` is a statement about what this
  // build emits, not a guarantee about the bytes — and reading `.amount_minor`
  // off it throws out of a composer the loader contract says never throws. The
  // whole screen would go to the error boundary, losing the three bands that
  // read fine along with the one cell that did not. accounts.ts:340 guards it
  // the same way, for the same reason.
  if (f.value === undefined || asRecord(f.value) === null) {
    return {
      ok: false,
      reason: f.value === undefined
        ? 'the backend reported this figure as available and sent no amount, which is not an amount of zero'
        : 'the backend reported this figure as available and sent something in place of the amount that is not an amount, which is not an amount of zero',
    }
  }

  // Law 12. No component on this console may render an analytics-sourced money
  // figure: the mirror is eventually consistent and never a source of truth,
  // and a balance quoted to a customer from it would be a balance nobody can
  // stand behind. Withheld, not badged.
  if (f.provenance?.authority === 'analytics') {
    return {
      ok: false,
      reason: 'refused — reported as analytics-sourced, and no money figure on this console may come from the analytics mirror (law 12)',
    }
  }

  const big = exactMinorBig(f.value.amount_minor)
  if (big === null) {
    return {
      ok: false,
      reason: 'the amount did not arrive as minor units this console can state exactly, so it is not shown rather than shown wrong in its last digits',
    }
  }

  // The full predicate, not `currency !== ''`. `format` throws on anything that
  // is not three letters — correctly, since an amount whose currency we cannot
  // name is not a value this system may carry (invariant 4) — but it runs
  // during render, so a `"EU"` off the wire would take the whole screen to the
  // error boundary instead of producing one named absence.
  if (!isCurrencyCode(f.value.currency)) {
    return {
      ok: false,
      reason: f.value.currency === ''
        ? 'the amount arrived with no currency at all, so it cannot be stated (invariant 4)'
        : `the amount arrived with ${JSON.stringify(f.value.currency)}, which is not an ISO-4217 code, so it cannot be stated (invariant 4)`,
    }
  }

  // Normalised rather than passed through: the ISO code renders verbatim beside
  // the number, and an operator comparing our figure against the institution's
  // should not have to decide whether `eur` and `EUR` are the same currency.
  const currency = f.value.currency.trim().toUpperCase()
  return {
    ok: true,
    minor: exactMinor(f.value.amount_minor),
    currency,
    text: format(money(big, currency)),
  }
}

/** The cell an unplaceable figure leaves behind. The reason is carried separately. */
const UNPLACED = 'not read'

function amountCell(p: Placement): Cell {
  if (!p.ok) return UNPLACED
  // Exact but too large for the design language's numeric amount cell: rendered
  // as its formatted self rather than dropped. The comparison band below states
  // it too, so the two agree.
  if (p.minor === null) return `${p.text} (beyond the exact integer range of an amount cell)`
  return [p.minor, p.currency]
}

// ---------------------------------------------------------------------------
// Which record a figure belongs to — per figure, never once for the screen
// ---------------------------------------------------------------------------

/**
 * The side of the reconciliation a figure is drawn under.
 *
 * `difference` is neither of the other two: `dashboard.Delta` composes it and
 * attributes it to `provider-confirmed`, because a difference only means
 * anything once the institution has answered.
 */
type Side = 'ours' | 'theirs' | 'difference'

const SIDE_AUTHORITY: Readonly<Record<Side, Authority>> = {
  ours: 'operational',
  theirs: 'provider-confirmed',
  difference: 'provider-confirmed',
}

const SIDE_STANDING: Readonly<Record<Side, string>> = {
  ours: 'our operational record',
  theirs: 'the institution’s record',
  difference: 'the difference between the two',
}

/**
 * Whether a shown figure's own provenance corroborates the side it is shown
 * under, and what to say when it does not.
 *
 * This is invariant 2 applied PER FIGURE, which is the only place it can be
 * applied truthfully. A single "ledger-sourced" badge on the screen would be a
 * claim about eight figures read from two stores, and it would keep being made
 * on the day one of them came back attributed to something else. The number is
 * still rendered when this fires — it was read, and hiding it would trade one
 * absence for another. What is withheld is the claim about whose record it is.
 */
function attributionProblem(f: FigureView, side: Side, who: string): string | null {
  const expected = SIDE_AUTHORITY[side]
  const label = text(f.label) ?? SIDE_STANDING[side]

  if (f.provenance === undefined) {
    return `${who}: “${label}” is drawn as ${SIDE_STANDING[side]} and carries no provenance at all, so which record it belongs to is not established (invariant 2)`
  }
  if (f.provenance.authority !== expected) {
    return `${who}: “${label}” is drawn as ${SIDE_STANDING[side]} and its provenance claims authority ${JSON.stringify(f.provenance.authority)} rather than ${JSON.stringify(expected)}, so which record it belongs to is not established (invariant 2)`
  }
  return null
}

/** What a figure's provenance says about where it was read, for the side header. */
function sourceOf(f: FigureView): string {
  const p = f.provenance
  if (p === undefined) return 'no provenance stated'
  const source = text(p.source) ?? 'source not stated'
  const at = stamp(p.read_at)
  return at === '' ? source : `${source} · read ${at}`
}

// ---------------------------------------------------------------------------
// One endpoint's answer
// ---------------------------------------------------------------------------

/**
 * One read, or the account of why there is none.
 *
 * Compose takes these rather than parsed payloads so the interesting half of
 * this file — what the screen does when one of four reads failed — is
 * table-testable with no client and no mocked rejection.
 */
export type Attempt =
  | { readonly ok: true; readonly payload: unknown }
  | {
      readonly ok: false
      readonly reason: string
      /** The HTTP status, when there was one. `null` means it never answered. */
      readonly status: number | null
      /** Never synthesised. `null` means the backend sent none. */
      readonly decisionRef: string | null
      /** A 403. Distinguished so a note can say a refusal came without a ref. */
      readonly refused: boolean
    }

type Failure = Extract<Attempt, { ok: false }>

/** The five answers this screen composes, named so a caller cannot transpose them. */
export interface Customer360Reads {
  readonly profile: Attempt
  readonly accounts: Attempt
  readonly postings: Attempt
  readonly events: Attempt
  /**
   * Every handle this customer attempted and what they were told (#111).
   *
   * The design brief puts it at tab 12 of the 360's thirteen rather than in the
   * Handles section, and the reason is worth keeping: a refusal is something you
   * look up about a PERSON. The route is per-customer and needs `customer.read`
   * — the customer permission, not `handle.read` — and there is no route
   * anywhere that lists claims across customers, so there is no left-rail
   * destination for it and there must not be one.
   */
  readonly handleClaims: Attempt
  /**
   * What this customer said about who pays them (#182, `rc-peruser`).
   *
   * Here rather than in Recognition, and for a stronger reason than the handle
   * journal's: the backend refuses a collection route on purpose, because
   * `?customer_id=` is one dropped filter away from enumerating everybody and
   * invariant 11b is exactly the rule a plausible edit must not break. There is
   * no left-rail destination for this and there must not be one.
   */
  readonly mappings: Attempt
}

function refLine(f: Failure, fallback: string): string {
  if (f.decisionRef !== null) return `decision_ref ${f.decisionRef}`
  if (f.refused) return 'The backend refused this read and sent no decision reference with it.'
  return fallback
}

// ---------------------------------------------------------------------------
// Payload guards
// ---------------------------------------------------------------------------

function isFigure(raw: unknown): raw is FigureView {
  const r = asRecord(raw)
  return r !== null && typeof r['label'] === 'string' && typeof r['availability'] === 'string'
}

function isProfileResponse(raw: unknown): raw is CustomerProfileResponse {
  const r = asRecord(raw)
  if (r === null) return false
  const c = asRecord(r['customer'])
  return c !== null && exactInteger(c['id']) !== null && typeof c['acceptance'] === 'string'
}

function isAccountsResponse(raw: unknown): raw is CustomerAccountsResponse {
  const r = asRecord(raw)
  return r !== null && typeof r['complete'] === 'boolean' && Array.isArray(r['accounts'])
}

function isPostingsResponse(raw: unknown): raw is CustomerPostingsResponse {
  const r = asRecord(raw)
  return r !== null && Array.isArray(r['postings']) && asRecord(r['page']) !== null
}

function isEventsResponse(raw: unknown): raw is CustomerEventsResponse {
  const r = asRecord(raw)
  return r !== null && Array.isArray(r['events']) && asRecord(r['page']) !== null
}

/**
 * A figure off an unvalidated row, or a stated absence.
 *
 * The guards above check the response's spine and not every leaf, because a
 * guard deep enough to reject one malformed figure would reject the whole
 * band — and losing three good accounts to one bad amount is a worse answer
 * than showing three accounts and one named gap.
 */
function figureAt(row: Record<string, unknown>, key: string, label: string): FigureView {
  const raw = row[key]
  if (isFigure(raw)) return raw
  return {
    label,
    availability: 'unavailable',
    reason: `the backend sent no readable ${label.toLowerCase()} for this row, so there is no figure here — this is not an amount of zero`,
  }
}

// ---------------------------------------------------------------------------
// Paging
// ---------------------------------------------------------------------------

interface PageRead {
  readonly limit: number | null
  readonly returned: number | null
  /** `null` when the backend did not say. Truncation unknown is not "complete". */
  readonly hasMore: boolean | null
  readonly nextCursor: number | null
}

function readPage(raw: unknown): PageRead {
  const r = asRecord(raw)
  if (r === null) return { limit: null, returned: null, hasMore: null, nextCursor: null }
  return {
    limit: exactInteger(r['limit']),
    returned: exactInteger(r['returned']),
    hasMore: typeof r['has_more'] === 'boolean' ? r['has_more'] : null,
    nextCursor: exactInteger(r['next_cursor']),
  }
}

/**
 * What the footer says about truncation — and it always says something.
 *
 * A page showing the first fifty of four thousand otherwise reads as the whole
 * set. So every list states its own completeness, including the ordinary case
 * where nothing was cut: "this is all of them" is information, and its absence
 * is what lets an operator conclude a customer has fifty postings when they
 * have four thousand.
 */
function pageFooter(page: PageRead, shown: number, noun: string): string {
  const cap = page.limit === null ? 'a page size the backend did not state' : `a page of ${String(page.limit)}`
  const count = `${String(shown)} ${noun}`

  if (page.hasMore === null) {
    return `${count} shown, under ${cap}. The backend did not say whether more exist, so whether this is the whole ${noun.replace(/s$/, '')} history is UNKNOWN — read it as truncated rather than complete.`
  }
  if (!page.hasMore) {
    return `${count} shown, under ${cap}. The backend reports no further rows: this is every one there is, not a page of them.`
  }
  const cursor = page.nextCursor === null
    ? 'and sent no cursor to continue from'
    : `continuing before id ${String(page.nextCursor)}`
  return `TRUNCATED — ${count} shown, under ${cap}, ${cursor}. More rows exist beyond this page and are not on this screen.`
}

/** Truncation is a gap in what the operator was shown, so it reaches the badge. */
function truncationGap(page: PageRead, noun: string): string | null {
  if (page.hasMore === null) return `whether the ${noun} was truncated`
  return page.hasMore ? `the rest of the ${noun} beyond the first page` : null
}

// ---------------------------------------------------------------------------
// Band 1 — who this is
// ---------------------------------------------------------------------------

/**
 * How an acceptance value is coloured, and what happens to one we do not know.
 *
 * A value this console has not learned is rendered verbatim in a NEUTRAL chip
 * with a note saying so, rather than coloured by a guess. Colour on this field
 * reads as a verdict — green means the institution took them on — and inventing
 * a verdict for an unrecognised state is a claim about a decision only the
 * institution makes (invariant 2).
 */
const ACCEPTANCE_TONE: Readonly<Record<string, Tone>> = {
  accepted: 'g',
  rejected: 'r',
  refused: 'r',
  withdrawn: 'n',
  pending: 'a',
  submitted: 'a',
  in_review: 'a',
}

function identityBand(customer: CustomerView): Block[] {
  const acceptance = text(customer.acceptance)
  const acceptanceTone: Tone = acceptance === null ? 'n' : ACCEPTANCE_TONE[acceptance] ?? 'n'
  const acceptanceNote = acceptance === null
    ? 'The backend sent no acceptance state, so the institution’s verdict is not known here.'
    : ACCEPTANCE_TONE[acceptance] === undefined
      ? 'This console does not recognise this acceptance state, so it is shown verbatim and deliberately not coloured as a verdict.'
      : text(customer.acceptance_reason) ?? ''

  const items: KvItem[] = [
    ['Customer id', String(customer.id), '', 'n', 'Ours. The institution’s own reference is below and is a different id space.', 'm'],
    ['Legal name', text(customer.legal_name) ?? 'not stated by the backend', '', 'n', '', ''],
    ['Email', text(customer.email) ?? 'not stated by the backend', '', 'n', '', 'm'],
    ['Kind', text(customer.kind) ?? 'not stated by the backend', '', 'n', '', ''],
    ['Country', text(customer.country_code) ?? 'not stated by the backend', '', 'n', 'Gating is fail-closed on this: no jurisdiction row means no activity (invariant 7).', ''],
    [
      'Acceptance',
      acceptance ?? 'not stated',
      acceptance ?? '',
      acceptanceTone,
      acceptanceNote,
      '',
    ],
    [
      'Institution subject reference',
      text(customer.institution_subject_ref) ??
        'none — this customer has never been submitted to the institution for a decision',
      '',
      'n',
      'Opaque and never parsed. Its absence is itself the answer to “why has nothing happened”.',
      'm',
    ],
    [
      'Onboarding SLA due',
      stampOr(customer.sla_due_at, 'not on the clock — no SLA due date is recorded'),
      '',
      'n',
      '',
      '',
    ],
    ['In this state since', stampOr(customer.state_entered_at, 'not stated by the backend'), '', 'n', '', ''],
    ['Customer since', stampOr(customer.created_at, 'not stated by the backend'), '', 'n', '', ''],
    ['Last updated', stampOr(customer.updated_at, 'not stated by the backend'), '', 'n', '', ''],
    [
      'Standing',
      customer.offboarded ? 'Offboarded' : 'Live',
      customer.offboarded ? 'offboarded' : 'live',
      customer.offboarded ? 'a' : 'g',
      customer.offboarded
        ? `Closed ${stampOr(customer.disabled_at, 'on a date the backend did not state')}. An offboarded customer is still reachable by id, by account number and by name — but not by email, which indexes live customers only.`
        : '',
      '',
    ],
  ]

  return [
    { t: 'kv', title: 'Who this is', src: 'ledger', items },
    {
      t: 'note',
      tone: 'n',
      kicker: 'Acceptance',
      title: 'The verdict above is the institution’s, and nothing here can move it',
      body: 'Acceptance is recorded as the institution decided it (invariant 2). This surface reads it; there is no route on the backend and no control on this screen that could set it, and a locally invented acceptance would be a decision we are not licensed to make.',
    },
  ]
}

// ---------------------------------------------------------------------------
// Band 2 — the accounts the institution issued
// ---------------------------------------------------------------------------

const ACCOUNT_COLS: readonly Column[] = [
  ['Account', 's', 'minmax(180px, 1.2fr)'],
  ['Currency', 'x', '90px'],
  ['Account number', 'm', 'minmax(150px, 1fr)'],
  ['State', 'c', '150px'],
  ['Ours', 'a', '170px'],
  ['The institution', 'a', '170px'],
  ['Difference', 'a', '170px'],
  ['Tied', 'c', '130px'],
]

/**
 * Account state as a badge, and only ever as a badge.
 *
 * `frozen` is imposed by the institution and displayed with its attribution —
 * console law 1. There is no tone that means "click to change this", because
 * there is nothing to click: the only state an operator may set is
 * `restricted`, that is `PermAccountRestrict`, and #42 does not wire it.
 */
const STATE_TONE: Readonly<Record<string, Tone>> = {
  open: 'g',
  active: 'g',
  restricted: 'a',
  frozen: 'r',
  closed: 'n',
}

function ibanCell(a: CustomerAccountView): Cell {
  const status = text(a.iban_status)
  if (status === 'issued') {
    const masked = text(a.iban_masked)
    // "Issued" with nothing to show is a read gap and says so. A blank cell
    // under an "issued" status would read as a number nobody bothered to copy.
    return masked ?? 'issued, but the backend sent no masked number'
  }
  if (status === 'pending') return 'not issued yet'
  if (status === null) return 'the backend stated no account-number status'
  // Fail closed on a status this console has not learned, the same way an
  // unknown availability does: the safe reading is that we cannot say.
  return `unknown status ${JSON.stringify(status)} — this console cannot say whether a number has been issued`
}

interface AccountRead {
  readonly row: readonly Cell[]
  /** The same account, as the state controls read it. */
  readonly standing: AccountStanding
  readonly comparison: Block
  readonly withheld: readonly string[]
  readonly attribution: readonly string[]
  readonly authorities: readonly string[]
}

function comparisonSide(f: FigureView, side: Side, fallbackLabel: string, meta: string): CmpSide {
  const placement = placeFigure(f)
  const label = text(f.label) ?? fallbackLabel
  const items: CmpItem[] = [
    ['Amount', placement.ok ? placement.text : 'not stated', placement.ok ? '' : 'a', 'm'],
  ]
  if (!placement.ok) items.push(['Why not', placement.reason, 'a', ''])

  // Both sides are `'n'`. `'v'` is this console's ANALYTICS tone — the colour
  // reserved for a figure from the ClickHouse mirror carrying a freshness
  // stamp — and painting the institution's provider-confirmed balance with it
  // says the opposite of what is true, on the one screen where law 12 forbids
  // an analytics-sourced balance outright. `placeFigure` above refuses any
  // figure claiming analytics authority; colouring provider money as analytics
  // would have the same file doing both. The institution's side is
  // distinguished by an edge, which is the corpus convention for this block.
  const side_: CmpSide = {
    label,
    src: sourceOf(f),
    tone: 'n',
    ...(side === 'theirs' ? { edge: 'g' as const } : {}),
    items,
    meta,
  }
  return side_
}

function readAccount(raw: unknown, index: number): AccountRead | null {
  const r = asRecord(raw)
  if (r === null) return null

  const id = exactInteger(r['id'])
  const ref = text(r['institution_account_ref'])
  const currency = text(r['currency'])
  const a: CustomerAccountView = {
    id: id ?? 0,
    institution_account_ref: ref ?? '',
    currency: currency ?? '',
    iban_status: typeof r['iban_status'] === 'string' ? r['iban_status'] : '',
    ...(typeof r['iban_masked'] === 'string' ? { iban_masked: r['iban_masked'] } : {}),
    ...(typeof r['iban_note'] === 'string' ? { iban_note: r['iban_note'] } : {}),
    state: typeof r['state'] === 'string' ? r['state'] : '',
    state_source: typeof r['state_source'] === 'string' ? r['state_source'] : '',
    ...(typeof r['state_reason'] === 'string' ? { state_reason: r['state_reason'] } : {}),
    state_entered_at: typeof r['state_entered_at'] === 'string' ? r['state_entered_at'] : '',
    created_at: typeof r['created_at'] === 'string' ? r['created_at'] : '',
    // Read rather than defaulted: these two date the two sides of the
    // comparison below, and a comparison whose sides are undated is a
    // comparison between our figure now and the institution's figure at some
    // time nobody stated. "Ours agrees with what they said last Tuesday" is not
    // agreement today.
    ...(typeof r['balance_updated_at'] === 'string' ? { balance_updated_at: r['balance_updated_at'] } : {}),
    ...(typeof r['provider_as_of'] === 'string' ? { provider_as_of: r['provider_as_of'] } : {}),
    // Three-state, not `?? 0`. Zero is a provisioning finding this screen
    // states as fact — "no ledger wallet account in this currency is paired
    // with this one" — and defaulting an unread field to it would make that
    // finding a claim over data nobody parsed. Absent reads as absent.
    wallet_matches: exactInteger(r['wallet_matches']),
    ledger: figureAt(r, 'ledger', 'Our ledger balance'),
    provider: figureAt(r, 'provider', 'The institution’s record'),
    delta: figureAt(r, 'delta', 'Difference'),
    // Also three-state. `=== true` maps absent, null and any non-boolean to
    // `false`, which on this screen is the RED "not tied" alarm — an operator
    // opening a discrepancy hunt on an account that ties, because one field
    // did not arrive. `readPage` reads `has_more` the same way and for the
    // same reason: "we could not tell" is not `false`.
    reconciled: typeof r['reconciled'] === 'boolean' ? r['reconciled'] : null,
  }

  const who = ref === null ? `account #${String(index + 1)}` : `account ${ref}`
  const ledger = placeFigure(a.ledger)
  const provider = placeFigure(a.provider)
  const delta = placeFigure(a.delta)

  const withheld: string[] = []
  for (const [placement, what] of [
    [ledger, 'our ledger balance'],
    [provider, 'the institution’s balance'],
    [delta, 'the difference between them'],
  ] as const) {
    if (!placement.ok) withheld.push(`${what} on ${who} — ${placement.reason}`)
  }

  const attribution: string[] = []
  for (const [figure, side] of [
    [a.ledger, 'ours'],
    [a.provider, 'theirs'],
    [a.delta, 'difference'],
  ] as const) {
    if (!placeFigure(figure).ok) continue
    const problem = attributionProblem(figure, side, who)
    if (problem !== null) attribution.push(problem)
  }

  const authorities: string[] = []
  for (const f of [a.ledger, a.provider, a.delta]) {
    if (f.provenance !== undefined) authorities.push(f.provenance.authority)
  }

  // `reconciled` is true ONLY when both sides were read and agree. An
  // unreadable position is not reconciled, and the chip must not let those two
  // read the same: "not tied" would send an operator hunting a discrepancy that
  // may not exist, and "tied" over an unread side is the false calm this whole
  // screen exists to prevent.
  //
  // The green chip is also cross-checked against the difference the console is
  // already holding. A payload that calls a position reconciled and sends a
  // difference of −2.50 cannot be both, and the number on the screen is the
  // one an operator can act on — so it takes "tied" away rather than being
  // contradicted by a chip beside it. balances.ts resolves the same
  // disagreement the same way and says so. A difference that could not be read
  // cannot confirm agreement either: `reconciled` alone is a claim about two
  // figures, and if one of them is unreadable the claim is unverifiable here.
  const agrees = delta.ok && delta.minor === 0
  const contradicted = a.reconciled === true && delta.ok && delta.minor !== 0
  const tied: Cell = !(ledger.ok && provider.ok)
    ? ['not comparable', 'a']
    : a.reconciled === null
      ? ['not stated', 'a']
      : contradicted
        ? ['disputed', 'r']
        : a.reconciled && agrees
          ? ['tied', 'g']
          : a.reconciled
            ? ['tied, unconfirmed', 'a']
            : ['not tied', 'r']

  const stateTone: Tone = STATE_TONE[a.state] ?? 'n'
  const stateChip: Cell = [text(a.state) ?? 'state not stated', stateTone]

  const accountCell: Cell = [
    ref ?? 'the backend stated no institution reference',
    id === null ? 'id not readable' : `id ${String(id)}`,
  ]

  const row: readonly Cell[] = [
    accountCell,
    currency ?? 'not stated',
    ibanCell(a),
    stateChip,
    amountCell(ledger),
    amountCell(provider),
    amountCell(delta),
    tied,
  ]

  const walletNote = a.wallet_matches === 1
    ? ''
    : a.wallet_matches === null
      ? 'The backend did not state how many ledger wallet accounts in this currency are paired with this one, so whether this account has exactly one is not known here.'
      : a.wallet_matches === 0
        ? 'No ledger wallet account in this currency is paired with this one.'
        : `${String(a.wallet_matches)} open ledger wallet accounts in this currency — a position cannot be attributed to this account without guessing.`

  const comparison: Block = {
    t: 'cmp',
    title: `${ref ?? who} — ours against the institution’s`,
    note: [
      `State ${text(a.state) ?? 'not stated'}, set by ${text(a.state_source) ?? 'a source the backend did not name'}${text(a.state_reason) === null ? '' : ` (${String(a.state_reason)})`}, since ${stampOr(a.state_entered_at, 'a time the backend did not state')}.`,
      text(a.iban_note) ?? '',
      walletNote,
    ].filter((s) => s !== '').join(' '),
    sides: [
      comparisonSide(
        a.ledger, 'ours', 'Our ledger balance',
        `last posting ${stampOr(a.balance_updated_at, 'not stated')}`,
      ),
      comparisonSide(
        a.provider, 'theirs', 'The institution’s record',
        `as of ${stampOr(a.provider_as_of, 'a time the institution did not state')}`,
      ),
    ],
    verdict: !delta.ok
      ? `The difference could not be stated: ${delta.reason} This is not agreement, and the chip beside it says so.`
      : contradicted
        ? `Disputed. The backend called this position reconciled and reported a difference of ${delta.text}, and those two cannot both be true. The number is what an operator can act on, so it wins: treat this as a discrepancy, and raise the payload itself as a second one.`
        : a.reconciled === true && agrees
          ? `Tied. The difference is ${delta.text}, and the institution's record is the one that wins any discrepancy (invariant 2).`
          : a.reconciled === true
            ? `Reported as reconciled, and the difference is ${delta.text} — exact agreement could not be confirmed from it here. The institution's record wins either way (invariant 2).`
            : a.reconciled === null
              ? `The backend did not state whether this position is reconciled. The difference is ${delta.text}; the institution's record wins any discrepancy (invariant 2).`
              : `Difference: ${delta.text}. The institution's record wins, and a correction is a compensating posting rather than an edit (invariant 2).`,
    note2: 'Read-only. Restrict and restore are Ops-only (PermAccountRestrict) and are deliberately not on this screen — #42 is the read side.',
  }

  const standing: AccountStanding = {
    id: a.id,
    currency: a.currency,
    state: a.state,
    source: a.state_source,
    reason: a.state_reason ?? '',
    since: a.state_entered_at,
  }
  return { row, standing, comparison, withheld, attribution, authorities }
}

interface BandResult {
  readonly blocks: readonly Block[]
  readonly missing: readonly string[]
  readonly authorities: readonly string[]
}

function failedBand(
  kicker: string, title: string, f: Failure, consequence: string, missing: string,
): BandResult {
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker,
      title,
      body: `${f.reason} ${consequence}`,
      ref: refLine(f, 'Nothing in this band was read.'),
    }],
    missing: [missing],
    authorities: [],
  }
}

function accountsBand(attempt: Attempt, writer: Writer | null): BandResult {
  if (!attempt.ok) {
    return failedBand(
      'Accounts',
      'The accounts could not be read',
      attempt,
      'This is NOT a customer with no account: whether the institution has issued them anything is unknown, and so is every balance that would hang off it.',
      'every account this customer holds, and both sides of every balance',
    )
  }
  if (!isAccountsResponse(attempt.payload)) {
    return failedBand(
      'Accounts',
      'The accounts endpoint returned a shape this console does not know',
      { ok: false, reason: 'The payload did not match the accounts response.', status: null, decisionRef: null, refused: false },
      'Nothing is rendered from it rather than partially rendered from a guess.',
      'every account this customer holds',
    )
  }

  const payload = attempt.payload
  const blocks: Block[] = []
  const missing: string[] = []
  const authorities: string[] = []

  if (payload.accounts.length === 0) {
    // The load-bearing distinction on this screen. `complete` is the backend
    // saying it looked and found none; without it, "no account" and "the list
    // was cut short" would render identically, and an agent would tell a
    // customer they hold nothing when the truth is we did not finish looking.
    blocks.push({
      t: 'note',
      tone: payload.complete ? 'n' : 'a',
      kicker: 'Accounts',
      title: payload.complete
        ? 'This customer holds no account at the institution'
        : 'No account was returned, and the list did not say it was complete',
      body: payload.complete
        ? `The read succeeded and found nothing: the institution has issued this customer no account yet, so there is no balance to show and no IBAN to quote. This is an answer, not a failed read. ${text(payload.note) ?? ''}`
        : 'The accounts endpoint returned an empty list without asserting completeness, so whether this customer holds no account or the list was cut short is unknown. Read it as unknown rather than as none.',
    })
    if (!payload.complete) missing.push('whether this customer holds any account at all')
    return { blocks, missing, authorities }
  }

  const rows: (readonly Cell[])[] = []
  const standings: AccountStanding[] = []
  const comparisons: Block[] = []
  const withheld: string[] = []
  const attribution: string[] = []
  let unreadable = 0

  payload.accounts.forEach((raw, index) => {
    const read = readAccount(raw, index)
    if (read === null) {
      unreadable += 1
      return
    }
    rows.push(read.row)
    standings.push(read.standing)
    comparisons.push(read.comparison)
    withheld.push(...read.withheld)
    attribution.push(...read.attribution)
    authorities.push(...read.authorities)
  })

  const footer = payload.complete
    ? `${String(rows.length)} account(s), and the backend states this is every one this customer holds, closed accounts included — an operator asking why money bounced is asking about the account that closed. This list is not paginated.`
    : `${String(rows.length)} account(s). The backend did NOT assert this list is complete, so read it as possibly cut short.`

  const table: TableBlock = {
    t: 'table',
    title: 'Accounts at the institution',
    // No `src` on the band: it carries both our figures and the institution's,
    // and one badge over both would be the screen-level claim invariant 2 says
    // must be made per figure. Each comparison below names its own source.
    cols: ACCOUNT_COLS,
    rows,
    facets: false,
    footer,
    foot2: 'Account numbers are masked by the backend. There is no permission on this console that unmasks one — a full account number is its own control and needs its own argument.',
  }
  blocks.push(table, ...comparisons)

  // Law 1's controls, one pair per account, after the table that named them.
  //
  // Per account rather than one control taking an id, because the id would be
  // typed and a mistyped account id restricts somebody who is not on the
  // screen. The accounts are already listed; the control belongs to a row.
  if (writer !== null) {
    for (const standing of standings) {
      blocks.push(...accountStateBlocks(writer.api, writer.caller, standing))
    }
  }

  if (!payload.complete) missing.push('accounts beyond the ones listed, if the list was cut short')
  if (unreadable > 0) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Accounts',
      title: `${String(unreadable)} account row could not be read`,
      body: 'The backend sent row(s) in a shape this console does not know. They are omitted rather than guessed at, so the table above is not every account the payload contained.',
    })
    missing.push(`${String(unreadable)} account row(s) in a shape this console could not read`)
  }
  if (withheld.length > 0) {
    blocks.push(withheldNote('Balances not shown', withheld))
    missing.push(...withheld)
  }
  if (attribution.length > 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Accounts',
      title: 'Some figures do not corroborate the record they are shown under',
      body: attribution.join(' · '),
      ref: 'invariant 2 — which record a figure belongs to is a per-figure claim',
    })
    missing.push(...attribution)
  }

  return { blocks, missing, authorities }
}

function withheldNote(title: string, reasons: readonly string[]): NoteBlock {
  return {
    t: 'note',
    tone: 'a',
    kicker: 'Not shown',
    title,
    body: `${reasons.join(' · ')}. Each of these renders as “${UNPLACED}” above rather than as a zero — an amount we could not read and an amount of zero are different facts about somebody's money.`,
  }
}

// ---------------------------------------------------------------------------
// Band 3 — the journal, as it touched this customer
// ---------------------------------------------------------------------------

const POSTING_COLS: readonly Column[] = [
  ['Posting', 'm', '120px'],
  ['What it was', 's', 'minmax(180px, 1.2fr)'],
  ['Effect on this customer', 'a', '200px'],
  ['Provider reference', 'm', 'minmax(150px, 1fr)'],
  ['Occurred', 'x', '180px'],
  ['Actor', 'x', 'minmax(120px, 1fr)'],
]

function journalBand(attempt: Attempt): BandResult {
  if (!attempt.ok) {
    return failedBand(
      'Journal',
      'This customer’s journal could not be read',
      attempt,
      'This is NOT an empty journal: whether anything has ever been posted to this customer’s accounts is unknown.',
      'every posting on this customer’s accounts',
    )
  }
  if (!isPostingsResponse(attempt.payload)) {
    return failedBand(
      'Journal',
      'The journal endpoint returned a shape this console does not know',
      { ok: false, reason: 'The payload did not match the postings response.', status: null, decisionRef: null, refused: false },
      'Nothing is rendered from it rather than partially rendered from a guess.',
      'every posting on this customer’s accounts',
    )
  }

  const payload = attempt.payload
  const page = readPage(payload.page)
  const blocks: Block[] = []
  const missing: string[] = []
  const authorities: string[] = []
  const withheld: string[] = []

  const rows: (readonly Cell[])[] = []
  let unreadable = 0

  for (const raw of payload.postings) {
    const r = asRecord(raw)
    if (r === null) {
      unreadable += 1
      continue
    }
    const id = exactInteger(r['id'])
    const net = figureAt(r, 'net', 'Net effect on this customer')
    const placement = placeFigure(net)
    if (net.provenance !== undefined) authorities.push(net.provenance.authority)
    if (!placement.ok) {
      withheld.push(`the net effect of posting ${id === null ? '(id unreadable)' : String(id)} — ${placement.reason}`)
    }

    rows.push([
      id === null ? 'id unreadable' : String(id),
      [
        text(r['posting_type']) ?? 'type not stated',
        text(r['kind']) ?? 'kind not stated',
      ],
      amountCell(placement),
      // Absent is meaningful here, not cosmetic: FR-5.5 says a posting that
      // moves customer value must carry the institution's reference. The list
      // does not read legs, so this console does NOT claim the posting moved
      // customer value — it reports the reference is absent and leaves the
      // inference to the operator, who can open the posting.
      text(r['provider_ref']) ?? 'none on this posting',
      stampOr(r['occurred_at'], 'not stated'),
      text(r['actor']) ?? 'not stated',
    ])
  }

  if (rows.length === 0 && unreadable === 0) {
    blocks.push({
      t: 'note',
      tone: 'n',
      kicker: 'Journal',
      title: 'Nothing has been posted to this customer’s accounts',
      body: `The read succeeded and returned no postings. This is an answer — no value has moved on this customer's accounts — and not a journal that failed to load. ${pageFooter(page, 0, 'postings')}`,
    })
  } else {
    blocks.push({
      t: 'table',
      title: 'Journal — what each posting did to this customer',
      src: 'ledger',
      cols: POSTING_COLS,
      rows,
      facets: false,
      footer: pageFooter(page, rows.length, 'postings'),
      foot2: text(payload.running_balance_note) ??
        'The backend states no running balance for this list.',
    })
  }

  const gap = truncationGap(page, 'journal')
  if (gap !== null) missing.push(gap)
  if (unreadable > 0) {
    missing.push(`${String(unreadable)} posting row(s) in a shape this console could not read`)
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Journal',
      title: `${String(unreadable)} posting row could not be read`,
      body: 'Omitted rather than guessed at. The table above is not every posting the payload contained.',
    })
  }
  if (withheld.length > 0) {
    blocks.push(withheldNote('Journal effects not shown', withheld))
    missing.push(...withheld)
  }

  blocks.push({
    t: 'note',
    tone: 'n',
    kicker: 'Journal',
    title: 'Append-only — the only action on a posting is a correction',
    body: 'Nothing on this band edits or deletes a posting, and nothing can: the journal is append-only and a correction is a compensating posting with a reason code (invariant 2, console law 5). This screen does not compose one either.',
  })

  return { blocks, missing, authorities }
}

// ---------------------------------------------------------------------------
// Band 4 — what the institution reported
// ---------------------------------------------------------------------------

const EVENT_COLS: readonly Column[] = [
  ['Event', 'm', '120px'],
  ['Reported by', 's', 'minmax(150px, 1fr)'],
  ['State', 'c', '140px'],
  ['Authorised', 'a', '170px'],
  ['Settled', 'a', '170px'],
  ['Value date', 'x', '170px'],
  ['Counterparty', 's', 'minmax(180px, 1.2fr)'],
]

const EVENT_STATE_TONE: Readonly<Record<string, Tone>> = {
  settled: 'g',
  authorized: 'a',
  authorised: 'a',
  pending: 'a',
  reversed: 'r',
  declined: 'r',
  failed: 'r',
}

function eventsBand(attempt: Attempt): BandResult {
  if (!attempt.ok) {
    return failedBand(
      'Institution events',
      'What the institution reported could not be read',
      attempt,
      'This is NOT an absence of events: whether the institution has reported anything on this customer’s accounts is unknown, which is precisely the question an agent is asked when money has not arrived.',
      'every event the institution reported for this customer',
    )
  }
  if (!isEventsResponse(attempt.payload)) {
    return failedBand(
      'Institution events',
      'The events endpoint returned a shape this console does not know',
      { ok: false, reason: 'The payload did not match the events response.', status: null, decisionRef: null, refused: false },
      'Nothing is rendered from it rather than partially rendered from a guess.',
      'every event the institution reported for this customer',
    )
  }

  const payload = attempt.payload
  const page = readPage(payload.page)
  const blocks: Block[] = []
  const missing: string[] = []
  const authorities: string[] = []
  const withheld: string[] = []

  const rows: (readonly Cell[])[] = []
  let unreadable = 0

  for (const raw of payload.events) {
    const r = asRecord(raw)
    if (r === null) {
      unreadable += 1
      continue
    }
    const id = exactInteger(r['id'])
    const who = id === null ? 'an event whose id could not be read' : `event ${String(id)}`

    const authorised = figureAt(r, 'authorised', 'Authorised amount')
    const settled = figureAt(r, 'settled', 'Settled amount')
    for (const f of [authorised, settled]) {
      if (f.provenance !== undefined) authorities.push(f.provenance.authority)
    }
    const auth = placeFigure(authorised)
    const sett = placeFigure(settled)
    if (!auth.ok) withheld.push(`the authorised amount on ${who} — ${auth.reason}`)

    // A settled amount is ABSENT until the institution settles, and that is the
    // ordinary case rather than a fault — the backend says so itself, sending
    // the figure as unavailable with its own reason. Listing that as withheld
    // would fire an alarm on the normal state, which trains an operator to
    // scroll past the band.
    //
    // But the backend ALSO sends a settled figure marked available, and a
    // figure this console cannot then place is a fault: an amount the
    // institution reported and we failed to scale. Both render as “not read”,
    // so the only thing separating them is whether the reason reaches the
    // reader. It does now — otherwise the operator sees an unremarkable cell
    // on a Live screen over a settlement that happened.
    if (!sett.ok && availabilityOf(settled.availability) === 'available') {
      withheld.push(`the settled amount on ${who} — ${sett.reason}`)
    }

    const state = text(r['state'])
    const stateCell: Cell = [state ?? 'state not stated', state === null ? 'n' : EVENT_STATE_TONE[state] ?? 'n']

    rows.push([
      id === null ? 'id unreadable' : String(id),
      [
        text(r['provider']) ?? 'provider not stated',
        `${text(r['direction']) ?? 'direction not stated'} · ${text(r['rail']) ?? 'rail not stated'}`,
      ],
      stateCell,
      amountCell(auth),
      amountCell(sett),
      stampOr(r['value_date'], 'no value date stated'),
      [
        text(r['counterparty_name']) ?? 'no counterparty name reported',
        text(r['counterparty_iban_masked']) ?? 'no account number reported',
      ],
    ])
  }

  if (rows.length === 0 && unreadable === 0) {
    blocks.push({
      t: 'note',
      tone: 'n',
      kicker: 'Institution events',
      title: 'The institution has reported nothing on this customer’s accounts',
      body: `The read succeeded and returned no events. That is an answer — nothing has been reported — and it is the answer an agent needs when a customer says money was sent: it did not arrive here either. ${pageFooter(page, 0, 'events')}`,
    })
  } else {
    blocks.push({
      t: 'table',
      title: 'What the institution reported',
      src: 'provider',
      cols: EVENT_COLS,
      rows,
      facets: false,
      footer: pageFooter(page, rows.length, 'events'),
      foot2: 'The state column, not the settled column, is where whether an event settled is stated. A settled cell reading “not read” is an amount that is not being shown — because the institution has not reported one yet, or because it reported one this console could not state — and it is never a settled amount of zero. Nothing in this band is summed: a position is on the accounts band and is capped by the institution’s own figure (invariant 2c).',
    })
  }

  const gap = truncationGap(page, 'event history')
  if (gap !== null) missing.push(gap)
  if (unreadable > 0) {
    missing.push(`${String(unreadable)} event row(s) in a shape this console could not read`)
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Institution events',
      title: `${String(unreadable)} event row could not be read`,
      body: 'Omitted rather than guessed at. The table above is not every event the payload contained.',
    })
  }
  if (withheld.length > 0) {
    blocks.push(withheldNote('Reported amounts not shown', withheld))
    missing.push(...withheld)
  }

  return { blocks, missing, authorities }
}

// ---------------------------------------------------------------------------
// Composition
// ---------------------------------------------------------------------------

const NO_CONTROL_NOTE: NoteBlock = {
  t: 'note',
  tone: 'n',
  quiet: true,
      kicker: 'What this screen can do',
  title: 'Read only, and structurally so',
  // Extended rather than joined by a second note: two statements of "this screen
  // does not write" are two things that can drift apart, and the one an operator
  // scrolled past is the one that was wrong.
  body: 'Nothing on this screen mutates anything. There is no freeze control — `frozen` is imposed by the institution and displayed with attribution (law 1) — no balance adjustment, no journal edit and no credential of any kind. Restrict and restore exist on the backend, are Ops-only, and are deliberately not wired here: #42 is the read side. No route in this console sets, changes, releases or retires a customer’s handle either: the claim journal below is evidence and carries no action at all, and its rule snapshot is the operator-only blocklist, so there is deliberately no share link and no send-to-customer affordance on it. The one handle-shaped act an operator can take is deciding a name already held for impersonation review, and that lives on its own queue.',
}

/**
 * A definite answer: nobody holds that id.
 *
 * Deliberately NOT the unreadable screen. A 404 is the backend having looked
 * and found nothing, which is a fact an operator can act on — they typed the id
 * wrongly, or the customer is in another environment. "We could not read this"
 * would send them to an incident channel over a typo. The provenance is `live`
 * for the same reason: the screen answered for everything it shows, and what it
 * shows is that there is nothing.
 */
function noSuchCustomer(id: number, readAt: string, contradiction: string | null): ScreenData {
  const blocks: Block[] = [{
    t: 'note',
    tone: 'a',
    kicker: 'Customer 360',
    title: `No customer holds id ${String(id)}`,
    body: 'The backend looked and found nobody with that id. This is an answer, not a failed read: there is no profile, no account, no journal and no reported event because there is no customer, rather than because something could not be reached. Check the id, and remember that a customer in another environment is not a customer here — staging carries no production data.',
  }]

  if (contradiction !== null) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Inconsistent',
      title: 'Another read answered for a customer the profile says does not exist',
      body: contradiction,
    })
  }

  blocks.push(NO_CONTROL_NOTE)
  return {
    blocks,
    provenance: { kind: 'live', source: SOURCE, authority: 'operational', readAt },
  }
}

/**
 * The screen when not one of the four reads answered.
 *
 * One note and nothing else. Not an empty profile with empty bands — that reads
 * as a customer who exists and holds nothing, which is the single most
 * dangerous sentence this screen can say to somebody chasing a payment — and
 * never the design corpus, whose invented customer has invented money.
 */
function unreadableScreen(reason: string, ref: string): ScreenData {
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: 'Customer 360',
      title: 'This screen could not be read',
      body: `${reason} Nothing is known here: not who this customer is, not what accounts they hold, not what has been posted and not what the institution has reported. That is different from a customer with nothing, and different again from a customer who does not exist.`,
      ref,
    }],
    provenance: { kind: 'unread', source: SOURCE, reason },
  }
}

/** Whether a band that should not have answered nonetheless returned rows. */
function answeredAnyway(reads: Customer360Reads): string | null {
  const claims: string[] = []
  if (reads.accounts.ok && isAccountsResponse(reads.accounts.payload)
    && reads.accounts.payload.accounts.length > 0) {
    claims.push('the accounts read returned accounts')
  }
  if (reads.postings.ok && isPostingsResponse(reads.postings.payload)
    && reads.postings.payload.postings.length > 0) {
    claims.push('the journal read returned postings')
  }
  if (reads.events.ok && isEventsResponse(reads.events.payload)
    && reads.events.payload.events.length > 0) {
    claims.push('the events read returned events')
  }
  // The fifth band, and it belongs in this list for the same reason as the other
  // three: a claim journal answering for a customer the profile says does not
  // exist is a contradiction, and one that goes unreported is one nobody chases.
  if (reads.handleClaims.ok && isHandleClaimsResponse(reads.handleClaims.payload)
    && reads.handleClaims.payload.claims.length > 0) {
    claims.push('the handle claim journal returned attempts')
  }
  if (claims.length === 0) return null
  return `The profile read answered 404 — no customer holds this id — but ${claims.join(', ')} for it in the same breath. One of the two is wrong and neither should be acted on until it is known which; nothing from those bands is rendered here.`
}

/** The stamp the backend put on its own answer, preferred over our receipt. */
function generatedAt(reads: Customer360Reads, at: Date): string {
  for (const attempt of [
    reads.profile, reads.accounts, reads.postings, reads.events, reads.handleClaims,
  ]) {
    if (!attempt.ok) continue
    const r = asRecord(attempt.payload)
    if (r === null) continue
    const when = stamp(r['generated_at'])
    if (when !== '') return when
  }
  return `${readAtStamp(at)} (console receipt — no band stated when it read)`
}

/**
 * Composes the four reads into one screen.
 *
 * Pure, and takes `at` explicitly: Part III — a helper with an ambient clock is
 * a helper that cannot be table-tested, and every interesting case on this
 * screen is a combination of four answers rather than one fetch.
 */
/**
 * What the accounts band needs in order to draw a state control.
 *
 * `null` on a surface that has neither — the composer stays pure and testable
 * without a client, and a caller that supplies nothing gets the read exactly as
 * it was before controls existed.
 */
export interface Writer {
  readonly api: ApiClient
  readonly caller: Caller
}

export function composeCustomer360(
  subjectID: number, reads: Customer360Reads, at: Date, writer: Writer | null = null,
): ScreenData {
  const readAt = generatedAt(reads, at)

  // 404 on the profile is not a failure and does not compose with the rest.
  if (!reads.profile.ok && reads.profile.status === 404) {
    return noSuchCustomer(subjectID, readAt, answeredAnyway(reads))
  }

  const failures = [
    reads.profile, reads.accounts, reads.postings, reads.events, reads.handleClaims,
    reads.mappings,
  ].filter((a): a is Failure => !a.ok)
  // SIX reads now, and the "nothing answered" screen must count all six. This
  // number has to move with the list above it every time: left behind, a screen
  // where every band failed composes one failure note per band instead of the
  // single honest sentence, and the bug is invisible until the day everything
  // is down — which is the day it matters.
  if (failures.length === 6) {
    const [first] = failures
    const reasons = failures.map((f) => f.reason).join(' ')
    return unreadableScreen(reasons, first === undefined ? 'Nothing was read.' : refLine(first, 'Nothing was read.'))
  }

  const blocks: Block[] = []
  const missing: string[] = []
  const authorities: string[] = []
  /**
   * Who this screen is about, once the profile read has said so.
   *
   * Starts absent and STAYS absent unless the profile answered with a shape
   * this console recognises — the shell then falls back to the nav label,
   * "Customer 360", which states where the operator is rather than whom they
   * are looking at.
   *
   * This is the field that used to be a literal. `screens/customers.ts` titled
   * the screen "Lina Vogt" and subtitled it "customers.id = 1042 · individual ·
   * DE · registered 2025-11-04 · language de-DE", and the shell drew both over
   * a 404. A name is the one thing here an operator will not second-guess, so
   * it may only ever be one the backend sent.
   */
  let identity: { title: string; purpose: string; subject: string } | null = null

  if (!reads.profile.ok) {
    const band = failedBand(
      'Who this is',
      'This customer’s profile could not be read',
      reads.profile,
      'The bands below may still have answered, but who they belong to is unconfirmed — do not read a name, an email or a standing off this screen.',
      'who this customer is, their standing and the institution’s verdict on them',
    )
    blocks.push(...band.blocks)
    missing.push(...band.missing)
  } else if (!isProfileResponse(reads.profile.payload)) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Who this is',
      title: 'The profile endpoint returned a shape this console does not know',
      body: 'Nothing is rendered from it rather than partially rendered from a guess. Whether the customer below is the one asked for is unconfirmed.',
    })
    missing.push('who this customer is')
  } else {
    blocks.push(...identityBand(reads.profile.payload.customer))
    authorities.push('operational')
    identity = identityOf(reads.profile.payload.customer)
  }

  for (const band of [
    accountsBand(reads.accounts, writer),
    journalBand(reads.postings),
    eventsBand(reads.events),
  ]) {
    blocks.push(...band.blocks)
    missing.push(...band.missing)
    authorities.push(...band.authorities)
  }

  // Last of the substantive bands, where the design puts it: tab 12 of 13. It
  // contributes no authority of its own — a handle is ours entirely, there is no
  // institution-side counterpart to lose a discrepancy to, and adding
  // `operational` a second time would say nothing the bands above have not.
  const handles = handleClaimsBand(reads.handleClaims)
  blocks.push(...handles.blocks)
  missing.push(...handles.missing)

  // Law 17's band, and the last one. It contributes no authority for the same
  // reason the handle journal does not: these are the customer's own words, and
  // there is no institution-side counterpart to lose a discrepancy to.
  const mappings = mappingsBand(reads.mappings)
  blocks.push(...mappings.blocks)
  missing.push(...mappings.missing)

  blocks.push(NO_CONTROL_NOTE)

  // `live` requires that the screen answered for everything it drew. Anything
  // in `missing` — a band that failed, a truncated page, a figure withheld —
  // makes the honest badge `partial`, and the badge is the only place that says
  // so once the note explaining it has scrolled off.
  const provenance: DataSource = missing.length === 0
    ? { kind: 'live', source: SOURCE, authority: authorityLabel(authorities), readAt }
    : { kind: 'partial', source: SOURCE, authority: authorityLabel(authorities), readAt, missing }

  return { blocks, provenance, ...(identity ?? {}) }
}

/**
 * The heading for a customer the profile read actually returned.
 *
 * Every clause is a field off the payload. Nothing is inferred and nothing is
 * defaulted: a customer whose `legal_name` came back empty is titled by their
 * id, because "Customer 1042" is true and a blank heading is not — whereas
 * inventing a display name is how this went wrong in the first place.
 *
 * `created_at` is sliced to its date part rather than reformatted into a
 * locale. An operator quoting the registration date needs the digits the
 * backend holds, not a rendering of them.
 */
function identityOf(c: CustomerView): { title: string; purpose: string; subject: string } {
  const parts = [
    `customers.id = ${String(c.id)}`,
    text(c.kind),
    text(c.country_code),
    text(c.created_at) === null ? null : `registered ${String(text(c.created_at)).slice(0, 10)}`,
  ].filter((x): x is string => x !== null)

  return {
    title: text(c.legal_name) ?? `Customer ${String(c.id)}`,
    purpose: parts.join(' · '),
    subject: `customers.id = ${String(c.id)}`,
  }
}

/**
 * The authorities that actually answered, derived rather than asserted.
 *
 * A hardcoded "operational" would keep saying operational on the day the
 * institution's figure is the only one that came back — which is the day it
 * matters most that the badge is honest about whose record the operator is
 * looking at.
 */
function authorityLabel(authorities: readonly string[]): string {
  const set = new Set(authorities)
  if (set.size === 0) return 'unattributed'
  return [...set].sort((a, b) => a.localeCompare(b)).join(' + ')
}

// ---------------------------------------------------------------------------
// Loading
// ---------------------------------------------------------------------------

/**
 * One read, resolved into an answer either way.
 *
 * An `AbortError` is rethrown: a navigation away is a cancellation, not a
 * degraded screen, and swallowing it would render "the journal could not be
 * read" over a screen the operator has already left.
 */
async function attempt(read: () => Promise<unknown>, subject: string): Promise<Attempt> {
  try {
    return { ok: true, payload: await read() }
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        ok: false,
        reason: `${subject} answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
        status: error.status,
        decisionRef: error.decisionRef,
        refused: error.isForbidden,
      }
    }
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return {
      ok: false,
      reason: `${subject} could not be reached.`,
      status: null,
      decisionRef: null,
      refused: false,
    }
  }
}

/**
 * Which customer the screen is about.
 *
 * `none` is a first-class answer rather than an error, because arriving at
 * Customer 360 without a subject is an ordinary thing to do — a bookmark, a
 * nav click, a link with the parameter dropped. The screen says so and reads
 * nothing, which is better than guessing at a customer.
 */
export type Subject =
  | { readonly kind: 'customer'; readonly id: number }
  | { readonly kind: 'none'; readonly reason: string }

/** The query parameter that names the customer. Filter state lives in the URL. */
export const SUBJECT_PARAM = 'customer'

/**
 * Reads the subject out of a query string.
 *
 * Pure, and takes the string rather than reaching for `location`, so the
 * interesting cases are table-testable. Every rejection is explicit:
 *
 *   - A non-integer, a zero or a negative is not an id the backend accepts, and
 *     sending one would spend a round trip to be told so.
 *   - An id past 2^53 is refused rather than truncated. It would resolve to a
 *     DIFFERENT customer — `Number.isSafeInteger` is the check, and it is the
 *     same reason `exactMinor` refuses an out-of-range amount: silently wrong
 *     in the last digits is the worst answer available.
 */
export function subjectFrom(search: string): Subject {
  const raw = new URLSearchParams(search).get(SUBJECT_PARAM)
  if (raw === null || raw.trim() === '') {
    return {
      kind: 'none',
      reason: `No customer was named. Customer 360 is about one customer, and the URL carries which one as ?${SUBJECT_PARAM}=<id>.`,
    }
  }
  if (!/^\d+$/.test(raw)) {
    return {
      kind: 'none',
      reason: `${JSON.stringify(raw)} is not a customer id. The backend requires a positive integer, so this was not sent.`,
    }
  }
  const id = Number(raw)
  if (!Number.isSafeInteger(id) || id <= 0) {
    return {
      kind: 'none',
      reason: `${JSON.stringify(raw)} is beyond the range of ids this console can carry exactly, so it was not sent — an id wrong in its last digits is a different customer.`,
    }
  }
  return { kind: 'customer', id }
}

/** The screen when no customer was named. Nothing is read and nothing is invented. */
export function noSubjectScreen(reason: string): ScreenData {
  return {
    blocks: [{
      t: 'note',
      tone: 'a',
      kicker: 'Customer 360',
      title: 'No customer has been chosen',
      body: `${reason} Nothing was read, and no customer is shown — a screen that picked one for you would be showing somebody's money to answer a question nobody asked.`,
    }, NO_CONTROL_NOTE],
    provenance: {
      kind: 'unread',
      source: SOURCE,
      reason: 'No customer was named, so no read was issued.',
    },
  }
}

/**
 * Reads one customer's profile, accounts, journal and reported events.
 *
 * All four in parallel and each folded in on its own. They are independent
 * reads against different tables, and serialising them would cost the screen
 * its slowest endpoint four times over — on the screen somebody is waiting on
 * while a customer holds the line. More importantly, `Promise.all` over
 * `attempt` never rejects: a band that failed becomes a band that says so,
 * rather than an exception that takes the other three down with it.
 *
 * Resolves rather than throwing, per the loader contract. A refusal keeps its
 * `decision_ref` (law 13) — the client parses it and a screen must not swallow
 * it.
 */
export function customer360Loader(id: number): ScreenLoader {
  return async (api) => {
    // The caller's own authority, read beside the five. `callerBeside` is what
    // keeps its promise from dangling when one of the others rejects.
    const calling = callerBeside(api)
    const [profile, accounts, postings, events, handleClaims, mappings] = await Promise.all([
      attempt(() => api.get<unknown>(customerProfilePath(id)), 'The customer profile'),
      attempt(() => api.get<unknown>(customerAccountsPath(id)), 'The customer’s accounts'),
      attempt(() => api.get<unknown>(customerPostingsPath(id)), 'The customer’s journal'),
      attempt(() => api.get<unknown>(customerEventsPath(id)), 'What the institution reported'),
      attempt(
        () => api.get<unknown>(handleClaimsPath(id)),
        'The customer’s handle claim journal',
      ),
      attempt(
        () => api.get<unknown>(customerMappingsPath(id)),
        'What the customer said about who pays them',
      ),
    ])
    return composeCustomer360(
      id, { profile, accounts, postings, events, handleClaims, mappings }, new Date(),
      { api, caller: await calling },
    )
  }
}

/**
 * The registry-shaped entry point.
 *
 * `ScreenLoader` takes only the client, so which customer is being looked at
 * has to come from somewhere else — and the console's answer to "where does
 * screen state live" is the URL, so that an operator hands a colleague a link
 * to the exact thing they are looking at rather than a description of how to
 * get there. Reading it here keeps the impurity in the thin fetch half:
 * `subjectFrom` and `composeCustomer360` are both pure and both tested without
 * a browser.
 */
export const loadCustomer360: ScreenLoader = async (api) => {
  const subject = subjectFrom(globalThis.location?.search ?? '')
  if (subject.kind === 'none') return noSubjectScreen(subject.reason)
  return customer360Loader(subject.id)(api)
}
